import React, { createContext, ReactNode, useContext } from 'react';
import { useQuery } from 'react-query';

import { getGroups } from 'apis/GroupAPI';
import { GroupType } from 'types/groups';
import useDataList from 'utils/hooks/useDataList';

import { GROUPS_PAGE_SIZE } from './constants';
import { GroupsFiltersType } from './table/types';

type GroupsContent = {
  items: GroupType[];
  loading: boolean;
  totalItemsCount: number;
  filters: Partial<GroupsFiltersType>;
  setPage: (page: number) => void;
  setFilters: (filtes: any) => void;
  resetFilters: () => void;
  refetch: () => void;
};

const GroupsContext = createContext<GroupsContent | undefined>(undefined);

export const useGroupsContext = () => {
  const context = useContext(GroupsContext);
  if (!context) throw new Error('useGroupsContext must be used within GroupsProvider');
  return context;
};

export const GroupsProvider = ({ children }: { children: ReactNode }) => {
  const { items, totalItemsCount, filters, setItems, setPage, setFilters } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: GROUPS_PAGE_SIZE,
        search: '',
        type: undefined,
        status: undefined,
        has_users: undefined,
        has_communities: undefined,
      },
    },
  });

  const { isLoading, isFetching, refetch } = useQuery({
    queryKey: [filters],
    queryFn: () =>
      getGroups({
        search: filters.search,
        page: filters.page,
        page_size: filters.pageSize,
        type: filters.type,
        status: filters.status,
        has_users: filters.has_users,
        has_communities: filters.has_communities,
      }),
    onSuccess: (data: any) => {
      setItems(data);
    },
    refetchOnWindowFocus: false,
  });

  const resetFilters = () => {
    setFilters({
      page: 1,
      search: '',
      status: [],
      type: [],
      has_users: [],
      has_communities: [],
    });
  };

  return (
    <GroupsContext.Provider
      value={{
        items: items as GroupType[],
        totalItemsCount,
        filters,
        setPage,
        setFilters,
        resetFilters,
        refetch,
        loading: isLoading || isFetching,
      }}
    >
      {children}
    </GroupsContext.Provider>
  );
};
